import { useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trash2, Delete, ToggleLeft, ToggleRight } from 'lucide-react';

const DISPLAY_MAP = { sqrt: '\u221A', pow: 'x\u02B8', pi: '\u03C0', fact: 'n!', abs: '|x|', pct: '%', frac: 'a/b' };

const CALC_MAP = {
  sqrt: 'Math.sqrt(', pow: 'Math.pow(', pi: 'Math.PI',
  ln: 'Math.log(', log: 'Math.log10(', e: 'Math.E', '^': '**',
  abs: 'Math.abs(', pct: '/100',
};

const TRIG_FN = ['sin', 'cos', 'tan'];

const ROWS = [
  ['sin', 'cos', 'tan', 'log'],
  ['ln', 'sqrt', 'abs', 'pi'],
  ['(', ')', '^', '/'],
  ['7', '8', '9', '*'],
  ['4', '5', '6', '-'],
  ['1', '2', '3', '+'],
  ['0', '.', 'pct', '='],
];

const MEMORY_BTNS = ['MC', 'MR', 'M+', 'M-'];

export const CalculatorPage = () => {
  const [display, setDisplay] = useState('0');
  const [expression, setExpression] = useState('');
  const [lastResult, setLastResult] = useState('');
  const [memory, setMemory] = useState(0);
  const [degMode, setDegMode] = useState(true); // true = degrees, false = radians
  const [fractionMode, setFractionMode] = useState(false);

  const toRad = (deg) => deg * Math.PI / 180;

  const handleButton = useCallback((btn) => {
    if (btn === '=') {
      try {
        let expr = expression || display;
        // Replace display symbols with JS equivalents
        Object.entries(CALC_MAP).forEach(([k, v]) => { expr = expr.split(k).join(v); });
        // Handle trig with degree mode
        TRIG_FN.forEach(fn => {
          if (degMode) {
            expr = expr.split(`${fn}(`).join(`Math.${fn}(Math.PI/180*`);
          } else {
            expr = expr.split(`${fn}(`).join(`Math.${fn}(`);
          }
        });
        // Handle factorial
        expr = expr.replace(/(\d+)!/g, (_, n) => {
          let r = 1; for (let i = 2; i <= parseInt(n); i++) r *= i; return r;
        });
        // Auto-close parentheses
        const openP = (expr.match(/\(/g) || []).length;
        const closeP = (expr.match(/\)/g) || []).length;
        for (let i = 0; i < openP - closeP; i++) expr += ')';
        const result = Function('"use strict"; return (' + expr + ')')();
        let formatted;
        if (!Number.isFinite(result)) {
          formatted = 'Error';
        } else if (fractionMode) {
          formatted = toFraction(result);
        } else {
          formatted = parseFloat(result.toPrecision(12)).toString();
        }
        setDisplay(formatted);
        setExpression('');
        setLastResult(String(parseFloat(result.toPrecision(12))));
      } catch {
        setDisplay('Error');
        setExpression('');
      }
      return;
    }

    if (btn === 'fact') {
      setDisplay(prev => prev === '0' ? '' : prev + '!');
      setExpression(prev => prev + '!');
      return;
    }

    if (display === '0' || display === 'Error' || lastResult) {
      const fnBtns = ['sin', 'cos', 'tan', 'log', 'ln', 'sqrt', 'pow', 'abs'];
      if (fnBtns.includes(btn)) {
        setDisplay(btn + '(');
        setExpression(btn + '(');
      } else if (['/', '*', '-', '+', '^', 'pct'].includes(btn) && lastResult) {
        setDisplay(lastResult + (DISPLAY_MAP[btn] || btn));
        setExpression(lastResult + btn);
      } else {
        setDisplay(DISPLAY_MAP[btn] || btn);
        setExpression(btn);
      }
      setLastResult('');
      return;
    }

    setDisplay(prev => prev + (DISPLAY_MAP[btn] || btn));
    setExpression(prev => prev + btn);
    setLastResult('');
  }, [display, expression, lastResult, degMode, fractionMode]);

  const handleMemory = (action) => {
    const currentVal = parseFloat(lastResult || display) || 0;
    switch (action) {
      case 'MC': setMemory(0); break;
      case 'MR': setDisplay(String(memory)); setExpression(String(memory)); setLastResult(''); break;
      case 'M+': setMemory(prev => prev + currentVal); break;
      case 'M-': setMemory(prev => prev - currentVal); break;
      default: break;
    }
  };

  const handleClear = () => { setDisplay('0'); setExpression(''); setLastResult(''); };
  const handleBackspace = () => {
    if (display.length <= 1 || display === 'Error') { handleClear(); return; }
    setDisplay(prev => prev.slice(0, -1));
    setExpression(prev => prev.slice(0, -1));
  };

  const toFraction = (decimal) => {
    if (Number.isInteger(decimal)) return String(decimal);
    const tolerance = 1e-9;
    let num = 1, den = 1, bestNum = 0, bestDen = 1;
    for (den = 1; den <= 10000; den++) {
      num = Math.round(decimal * den);
      if (Math.abs(num / den - decimal) < tolerance) {
        bestNum = num; bestDen = den; break;
      }
      if (Math.abs(num / den - decimal) < Math.abs(bestNum / bestDen - decimal)) {
        bestNum = num; bestDen = den;
      }
    }
    if (bestDen === 1) return String(bestNum);
    return `${bestNum}/${bestDen}`;
  };

  const getButtonStyle = (btn) => {
    if (btn === '=') return 'bg-teal-600 hover:bg-teal-700 text-white text-lg font-bold shadow-sm';
    if (['/', '*', '-', '+', '^'].includes(btn)) return 'bg-teal-50 hover:bg-teal-100 text-teal-700 font-semibold text-lg';
    if (['sin', 'cos', 'tan', 'log', 'ln', 'sqrt', 'pow', 'abs', 'pi', 'e', '(', ')', 'pct', 'fact', 'frac'].includes(btn))
      return 'bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] font-medium';
    return 'bg-white hover:bg-slate-50 text-slate-900 text-lg font-medium border border-slate-200';
  };

  return (
    <Layout title="Calculator" breadcrumb={[{ label: 'Classes', path: '/app/dashboard' }, { label: 'Calculator' }]}>
      <div className="max-w-sm mx-auto" data-testid="calculator-page">
        <Card className="bg-white border border-slate-200 overflow-hidden shadow-soft">
          <CardContent className="p-0">
            {/* Display */}
            <div className="bg-slate-900 p-5 rounded-b-none">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDegMode(!degMode)}
                    className="text-[11px] font-mono text-slate-400 hover:text-white transition-colors px-2 py-0.5 rounded border border-slate-700"
                    data-testid="deg-rad-toggle"
                  >
                    {degMode ? 'DEG' : 'RAD'}
                  </button>
                  <button
                    onClick={() => setFractionMode(!fractionMode)}
                    className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${
                      fractionMode ? 'text-indigo-400 border-teal-500' : 'text-slate-400 border-slate-700 hover:text-white'
                    }`}
                    data-testid="fraction-toggle"
                  >
                    a/b
                  </button>
                </div>
                {memory !== 0 && <span className="text-[11px] text-amber-400 font-mono">M</span>}
              </div>
              {expression && <div className="text-slate-500 text-sm text-right h-5 truncate font-mono">{expression}</div>}
              <div className="text-white text-right text-3xl font-heading font-bold truncate mt-1" data-testid="calc-display">
                {display}
              </div>
            </div>

            {/* Memory Row */}
            <div className="grid grid-cols-4 gap-1 p-2 pb-0">
              {MEMORY_BTNS.map(btn => (
                <button
                  key={btn}
                  className="h-9 rounded-lg text-xs font-medium bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition-colors"
                  onClick={() => handleMemory(btn)}
                  data-testid={`calc-mem-${btn.toLowerCase()}`}
                >
                  {btn}
                </button>
              ))}
            </div>

            {/* Clear / Factorial / Backspace */}
            <div className="grid grid-cols-3 gap-1 p-2 pb-0">
              <Button variant="outline" className="h-11 border-rose-200 text-rose-600 hover:bg-rose-50 font-medium text-sm" onClick={handleClear} data-testid="calc-clear">
                AC
              </Button>
              <button
                className="h-11 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors"
                onClick={() => handleButton('fact')}
                data-testid="calc-btn-fact"
              >
                n!
              </button>
              <Button variant="outline" className="h-11 border-slate-200 text-slate-500 hover:bg-slate-50 font-medium text-sm" onClick={handleBackspace} data-testid="calc-backspace">
                <Delete className="w-4 h-4" />
              </Button>
            </div>

            {/* Button grid */}
            <div className="p-2 space-y-1">
              {ROWS.map((row, i) => (
                <div key={i} className="grid grid-cols-4 gap-1">
                  {row.map(btn => (
                    <button
                      key={btn}
                      className={`h-11 rounded-lg transition-colors ${getButtonStyle(btn)}`}
                      onClick={() => handleButton(btn)}
                      data-testid={`calc-btn-${btn}`}
                    >
                      {DISPLAY_MAP[btn] || btn}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
